import { useEffect, useMemo, useState } from 'preact/hooks';
import type { PageProps } from '../app';
import { pemDecodeAll } from '../core/bytes/encoding';
import { acceptsExpiry, isActiveLog, type LogDescriptor } from '../core/log/client';
import { embeddedSctEntry, verifyScts, type SctVerdict } from '../core/sct/embedded';
import type { Sct, SignedEntry } from '../core/rfc6962/types';
import { TransportError } from '../core/transport/errors';
import { parseCertificate, type ParsedCert } from '../core/x509/parse';
import { href, withFrom } from '../router';
import { Badge } from '../ui/components/Badge';
import { ErrorBox } from '../ui/components/ErrorBox';
import { Icon } from '../ui/components/Icon';
import { KindBadge, ProgramBadges } from '../ui/components/LogBadges';
import { Card } from '../ui/components/Section';
import { Spinner } from '../ui/components/Spinner';
import { TimeStamp } from '../ui/components/TimeStamp';
import { fmtInt } from '../ui/format';
import { sctAddChainJson, sctBundleFilename, sctBundleJson, sctListBytes, sctListFilename, singleSctFilename, type SctRecord } from '../ui/sctExport';
import { inclusionLinksFor, locateSctEntry } from '../ui/sctLinks';
import { getClient } from '../ui/state/clients';
import { registry, registryState } from '../ui/state/logs';
import { SAMPLE_CHAIN_PEM, SAMPLE_PRECERT_CHAIN_PEM } from '../ui/sample';
import { ChainDiscovery } from '../ui/components/ChainDiscovery';
import { HostFetch } from '../ui/components/HostFetch';
import { ScrollBox } from '../ui/components/ScrollBox';
import { SOURCE_LABEL, type DiscoveredChain } from '../core/chain/types';
import { takeSubmitPrefill } from '../ui/state/prefill';
import { usePageState } from '../ui/state/pageState';
import { describeRoute, logFromRoute, logPath } from '../ui/nav';
import { Breadcrumbs } from '../ui/components/Breadcrumbs';
import { ReturnStrip } from '../ui/components/ReturnStrip';
import { ExportMenu } from '../ui/components/Export';

interface Parsed {
  ders: Uint8Array[];
  leaf?: ParsedCert;
  chain: ParsedCert[];
  error?: string;
}

interface Result {
  log: LogDescriptor;
  status: 'pending' | 'ok' | 'error';
  sct?: Sct;
  verdict?: SctVerdict;
  entryIndex?: number;
  inclusionLink?: string;
  error?: unknown;
}

/** Submit a chain to one or many logs at once; verify and save the SCTs. */
export function SubmitPage({ query }: PageProps) {
  const preselect = registry.value.byIdPrefix(query.get('log') ?? '')?.id;
  const from = query.get('from');
  const fromEntry = (from && describeRoute(from)?.startsWith('entry #')) || false;
  const fromLog = !fromEntry && !!from && logFromRoute(from) !== undefined;
  return (
    <main class="content">
      <Breadcrumbs items={[{ label: 'Home', href: href('/') }, { label: 'Submit' }]} />
      <div class="page-head">
        <div>
          <h1>Submit a certificate chain</h1>
          <p class="lede">Send a certificate or precertificate to one or more logs (<code>add-chain</code> / <code>add-pre-chain</code>), verify every SCT you get back, and save them. Submitting publishes the certificate permanently.</p>
        </div>
      </div>
      <ReturnStrip query={query} note={fromEntry ? 'the chain from that entry is loaded below' : fromLog && preselect ? 'that log is pre-ticked below; add others if you like' : undefined} />
      <SubmitBody {...(preselect ? { preselect } : {})} query={query} />
    </main>
  );
}

function SubmitBody({ preselect }: { preselect?: string; query: URLSearchParams }) {
  const reg = registry.value;
  // Working state survives leaving the page (e.g. to check an entry) and coming back.
  const [text, setText] = usePageState('submit:text', '');
  const [discovered, setDiscovered] = usePageState<DiscoveredChain | undefined>('submit:discovered', undefined);
  const [selected, setSelected] = usePageState<Set<string>>('submit:selected', () => new Set(preselect ? [preselect] : []));
  const [filter, setFilter] = usePageState('submit:filter', '');
  const [onlyMatching, setOnlyMatching] = usePageState('submit:onlyMatching', true);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = usePageState<Result[] | undefined>('submit:results', undefined);
  const [entry, setEntry] = usePageState<SignedEntry | undefined>('submit:entry', undefined);

  useEffect(() => {
    // A chain handed over from an entry page ("Submit to other logs") replaces whatever was here.
    const prefill = takeSubmitPrefill();
    if (prefill) { setText(prefill); setResults(undefined); setEntry(undefined); setDiscovered(undefined); }
  }, []);
  useEffect(() => {
    if (preselect) setSelected(new Set([preselect]));
  }, [preselect]);

  const parsed = useMemo<Parsed | undefined>(() => {
    const blocks = pemDecodeAll(text);
    if (blocks.length === 0) return undefined;
    try {
      const ders = blocks.map((b) => b.der);
      const [leaf, ...rest] = ders.map((d) => parseCertificate(d)) as [ParsedCert, ...ParsedCert[]];
      return { ders, leaf, chain: rest };
    } catch (e) {
      return { ders: [], chain: [], error: e instanceof Error ? e.message : String(e) };
    }
  }, [text]);
  const precert = parsed?.leaf?.hasPoison ?? false;
  const top = parsed?.chain.length ? parsed.chain[parsed.chain.length - 1] : parsed?.leaf;
  const chainComplete = top !== undefined && top.subject === top.issuer;

  const candidates = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return reg
      .all()
      .filter((l) => isActiveLog(l) || l.custom || l.id === preselect)
      .filter((l) => !onlyMatching || !parsed?.leaf || acceptsExpiry(l, parsed.leaf.notAfter) || l.id === preselect)
      .filter((l) => !needle || `${l.description} ${l.operator}`.toLowerCase().includes(needle))
      .sort((a, b) => a.operator.localeCompare(b.operator) || a.description.localeCompare(b.description));
  }, [reg, filter, onlyMatching, parsed, preselect]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = (logs: LogDescriptor[]) => setSelected(new Set(logs.map((l) => l.id)));

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!parsed?.leaf || selected.size === 0) return;
    setBusy(true);
    const logs = candidates.filter((l) => selected.has(l.id));
    setResults(logs.map((log) => ({ log, status: 'pending' })));
    let signedEntry: SignedEntry | undefined;
    let entryProblem: string | undefined;
    if (precert) {
      const ctx = await embeddedSctEntry(parsed.ders[0]!, parsed.ders.slice(1));
      signedEntry = ctx.entry;
      entryProblem = ctx.problem;
    } else {
      signedEntry = { type: 'x509', cert: parsed.ders[0]! };
    }
    setEntry(signedEntry);
    await Promise.all(
      logs.map(async (log) => {
        let r: Result;
        try {
          const sct = await getClient(log).addChain(parsed.ders, precert);
          const [verdict] = signedEntry ? await verifyScts([sct], signedEntry, () => log.key) : [{ ok: 'unverifiable', reason: entryProblem ?? 'entry unavailable' } as SctVerdict];
          const [link] = await inclusionLinksFor([sct], signedEntry);
          r = { log, status: 'ok', sct, verdict: verdict!, ...(link ? { inclusionLink: link } : {}) };
          const located = await locateSctEntry(sct, signedEntry);
          if (located.status === 'found') r.entryIndex = located.index;
        } catch (error) {
          r = { log, status: 'error', error };
        }
        setResults((prev) => prev?.map((x) => (x.log.id === log.id ? r : x)));
      }),
    );
    setBusy(false);
  };

  const okRecords: SctRecord[] = (results ?? []).filter((r) => r.status === 'ok' && r.sct).map((r) => ({ log: r.log, sct: r.sct!, verified: r.verdict?.ok === true ? true : r.verdict?.ok === false ? false : 'unverifiable' }));
  const subject = parsed?.leaf?.commonName ?? 'certificate';

  return (
    <div>
      <form onSubmit={submit} class="stack">
        <Card title={<><Icon name="file" /> 1 · Chain</>}>
          <label class="field">
            <span>PEM chain <span class="hint">leaf or precertificate first, then intermediates up to a root the logs accept</span></span>
            <textarea class="input" rows={8} value={text} onInput={(e) => { setText((e.target as HTMLTextAreaElement).value); setDiscovered(undefined); }} placeholder={'-----BEGIN CERTIFICATE-----\n(leaf or precertificate)\n-----END CERTIFICATE-----\n-----BEGIN CERTIFICATE-----\n(intermediate)\n-----END CERTIFICATE-----'} spellcheck={false} />
          </label>
          {!text && (
            <div class="stack" style="gap:0.4rem;margin-top:0.5rem">
              <HostFetch onChain={(pem, r) => { setDiscovered(r); setText(pem); }} />
              <div class="row">
                <span class="small muted">or load an example (example.com, already logged):</span>
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setText(SAMPLE_CHAIN_PEM)}><Icon name="file" /> certificate → add-chain</button>
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setText(SAMPLE_PRECERT_CHAIN_PEM)}><Icon name="file" /> precertificate → add-pre-chain</button>
              </div>
            </div>
          )}
          {parsed?.error && <div class="errorbox" style="margin-top:0.6rem">Could not parse the chain: {parsed.error}</div>}
          {parsed?.leaf && (
            <div class="notice info" style="margin-top:0.6rem">
              <Badge tone="muted">{parsed.ders.length} certificate{parsed.ders.length === 1 ? '' : 's'}</Badge>
              <span><strong>{parsed.leaf.commonName}</strong> issued by {/CN=([^,]+)/.exec(parsed.leaf.issuer)?.[1] ?? parsed.leaf.issuer}, expires {parsed.leaf.notAfter.toISOString().slice(0, 10)}</span>
              {precert ? <Badge tone="warn">precertificate → add-pre-chain</Badge> : <Badge tone="info">certificate → add-chain</Badge>}
              {parsed.chain.length === 0 && <Badge tone="warn">no issuer included — most logs reject bare leaves</Badge>}
              {parsed.chain.length > 0 && !chainComplete && <Badge tone="muted">chain does not end at a root</Badge>}
              {chainComplete && <Badge tone="ok">chain ends at a root</Badge>}
              {discovered && (
                <span class="small muted" title={discovered.detail}>
                  {discovered.detail.includes('served by') ? `fetched from ${discovered.detail.replace(/.*served by /, '')} via` : `chain of ${discovered.chain.length - 1} issuer${discovered.chain.length === 2 ? '' : 's'} from`} {SOURCE_LABEL[discovered.source]}: {discovered.subjects.slice(1).map((x) => /CN=([^,]+)/.exec(x)?.[1] ?? x).join(' → ')}
                </span>
              )}
            </div>
          )}
          {parsed?.leaf && !chainComplete && (
            <div style="margin-top:0.6rem">
              <ChainDiscovery chain={parsed.ders} onChain={(pem, r) => { setDiscovered(r); setText(pem); }} label={parsed.chain.length === 0 ? 'Find the issuer chain' : 'Complete the chain'} />
            </div>
          )}
        </Card>

        <Card
          title={<><Icon name="list" /> 2 · Logs <span class="badge badge-muted">{selected.size} selected</span></>}
          actions={
            <>
              <input class="input input-sm" style="width:200px" type="search" placeholder="Filter logs" value={filter} onInput={(e) => setFilter((e.target as HTMLInputElement).value)} />
              <label class="check" title="Only logs whose temporal interval covers the certificate's expiry"><input type="checkbox" checked={onlyMatching} onChange={(e) => setOnlyMatching((e.target as HTMLInputElement).checked)} /> accepts this expiry</label>
              <button type="button" class="btn btn-sm" onClick={() => selectAll(candidates)}>Select all ({candidates.length})</button>
              <button type="button" class="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
            </>
          }
          foot={<>Only logs that are usable / qualified / read-only in Chrome's or Apple's list are offered. Tiled logs answer with the entry index immediately; RFC 6962 logs incorporate entries within their MMD. Anyone can (re)submit a certificate <em>or a precertificate</em> — logs only check that the chain leads to a root they accept and the expiry falls in their interval; some logs de-duplicate and return the original SCT.</>}
        >
          {registryState.value.status === 'loading' && candidates.length === 0 && <Spinner label="Loading log lists…" />}
          {!parsed?.leaf && <p class="small muted">Paste a chain first — logs are filtered by the certificate's expiry date.</p>}
          <ScrollBox count={candidates.length} itemLabel="logs">
            <table class="table">
              <tbody>
                {candidates.map((l) => (
                  <tr key={l.id} class="clickable" onClick={() => toggle(l.id)}>
                    <td style="width:1%"><input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${l.description}`} /></td>
                    <td class="col-log"><strong>{l.description}</strong><div class="tiny muted">{l.operator}</div></td>
                    <td class="col-kind"><KindBadge kind={l.kind} /></td>
                    <td><ProgramBadges log={l} compact /></td>
                    <td class="col-expiry small muted nowrap">{l.temporalInterval ? `${l.temporalInterval.start.slice(0, 10)} → ${l.temporalInterval.end.slice(0, 10)}` : 'any expiry'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {candidates.length === 0 && registryState.value.status !== 'loading' && <div class="empty">No logs match{onlyMatching && parsed?.leaf ? ' this certificate\'s expiry — untick "accepts this expiry" to see all' : ''}.</div>}
          </ScrollBox>
        </Card>

        <div class="form-actions">
          <button type="submit" class="btn btn-primary" disabled={busy || !parsed?.leaf || selected.size === 0}><Icon name="upload" /> {busy ? 'Submitting…' : `Submit to ${selected.size} log${selected.size === 1 ? '' : 's'}`}</button>
          <span class="small muted">Tip: <code>openssl s_client -connect host:443 -showcerts</code> prints a site's chain.</span>
        </div>
      </form>

      {results && (
        <Card
          class="stack-top"
          title={<><Icon name="shield" /> 3 · SCTs <span class="badge badge-muted">{okRecords.length} / {results.length}</span></>}
          actions={
            okRecords.length > 0 ? (
              <ExportMenu label="All SCTs" formats={[
                { label: 'JSON bundle', filename: sctBundleFilename(subject), mime: 'application/json', hint: 'add-chain responses + TLS encodings', text: () => sctBundleJson(okRecords, subject) },
                { label: 'SignedCertificateTimestampList', filename: sctListFilename(subject), hint: 'TLS list (.sctlist)', bytes: () => sctListBytes(okRecords) },
              ]} />
            ) : undefined
          }
          foot={<>JSON bundle = each SCT as an add-chain response plus TLS encodings. <code>.sct</code> = one raw TLS SCT (what web servers consume); <code>.sctlist</code> = the TLS list of all of them.</>}
        >
          <div class="table-wrap">
            <table class="table">
              <thead>
                <tr><th>Log</th><th>Result</th><th>Timestamp</th><th>Entry</th><th>Signature</th><th></th></tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.log.id}>
                    <td><a href={withFrom(logPath(r.log))}>{r.log.description}</a> <KindBadge kind={r.log.kind} /></td>
                    <td>
                      {r.status === 'pending' && <Spinner label="submitting…" />}
                      {r.status === 'ok' && <Badge tone="ok"><Icon name="check" size={12} /> SCT</Badge>}
                      {r.status === 'error' && <ResultError error={r.error} />}
                    </td>
                    <td class="small nowrap">{r.sct ? <TimeStamp ms={r.sct.timestamp} relative={false} /> : '—'}</td>
                    <td class="nowrap">{r.entryIndex !== undefined ? <a href={withFrom(logPath(r.log, `entry/${r.entryIndex}`))}>#{fmtInt(r.entryIndex)}</a> : r.status === 'ok' ? <span class="muted tiny">after MMD</span> : '—'}</td>
                    <td>
                      {r.verdict?.ok === true && <Badge tone="ok">valid</Badge>}
                      {r.verdict?.ok === false && <><Badge tone="err">INVALID</Badge>{r.verdict.error && <div class="tiny">{r.verdict.error}</div>}</>}
                      {r.verdict?.ok === 'unverifiable' && <Badge tone="warn" title={r.verdict.reason}>not verified</Badge>}
                    </td>
                    <td class="nowrap row" style="justify-content:flex-end">
                      {r.sct && (
                        <>
                          <ExportMenu label="SCT" formats={[
                            { label: 'TLS SCT', filename: singleSctFilename({ log: r.log, sct: r.sct, verified: true }, subject), hint: '.sct (nginx / Apache)', bytes: r.sct.raw },
                            { label: 'add-chain JSON', filename: singleSctFilename({ log: r.log, sct: r.sct, verified: true }, subject).replace(/\.sct$/, '.json'), mime: 'application/json', text: () => sctAddChainJson(r.sct!) },
                          ]} />
                          {r.inclusionLink && <a class="btn btn-sm" href={withFrom(r.inclusionLink)}><Icon name="shield" /> Inclusion</a>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {entry === undefined && precert && results.some((r) => r.status === 'ok') && <p class="small muted" style="margin-top:0.6rem">Signatures could not be checked: the precert entry needs the CA certificate right after the precertificate.</p>}
        </Card>
      )}
    </div>
  );
}

function ResultError({ error }: { error: unknown }) {
  if (error instanceof TransportError) {
    if (error.kind === 'cors-blocked') return <Badge tone="err" title={error.hint}>blocked (CORS)</Badge>;
    if (error.kind === 'http') return <><Badge tone="err">HTTP {error.status}</Badge><div class="tiny muted">{error.status === 400 ? 'rejected (chain / root / expiry not accepted)' : error.message}</div></>;
  }
  return <><Badge tone="err">failed</Badge><div class="tiny muted">{error instanceof Error ? error.message : String(error)}</div></>;
}
